import React, { useState, useEffect, useCallback } from 'react';
import { useAuthContext } from '../../context/AuthContext';
import { authorizationApi } from '../../services/authorizationApiService';
import { toastService } from '../../services/toastService';
import './SecurityTab.css';

export default function SecurityTab({ isLoading }) {
  const { changePassword, globalSignOut } = useAuthContext();

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  const [passwordPolicy, setPasswordPolicy] = useState(null);


  // const [sessions, setSessions] = useState([
  //   {
  //     id: '1',
  //     device: 'Chrome on MacBook Pro',
  //     location: 'Barcelona, Spain',
  //     lastActive: '2024-01-15T10:30:00Z',
  //     isCurrent: true,
  //   },
  //   {
  //     id: '2',
  //     device: 'Safari on iPhone',
  //     location: 'Barcelona, Spain',
  //     lastActive: '2024-01-14T18:45:00Z',
  //     isCurrent: false,
  //   },
  // ]);

  //fetch password policy when the form opens
  useEffect(() => {
    if (!showPasswordForm) return;
    let cancelled = false;
    authorizationApi.getPasswordPolicy()
      .then((policy) => {
        if (!cancelled) setPasswordPolicy(policy);
      })
      .catch((err) => {
        console.error('Failed to fetch password policy:', err);
      });
    return () => { cancelled = true; };
  }, [showPasswordForm]);

  //fetch which requirements are met for the current new password
  const getPasswordRequirements = useCallback(() => {
    if (!passwordPolicy) return [];
    const pw = passwordData.newPassword;
    return [
      { label: `At least ${passwordPolicy.MinimumLength} characters`, met: pw.length >= passwordPolicy.MinimumLength },
      ...(passwordPolicy.RequireUppercase ? [{ label: 'At least one uppercase letter', met: /[A-Z]/.test(pw) }] : []),
      ...(passwordPolicy.RequireLowercase ? [{ label: 'At least one lowercase letter', met: /[a-z]/.test(pw) }] : []),
      ...(passwordPolicy.RequireNumbers ? [{ label: 'At least one number', met: /[0-9]/.test(pw) }] : []),
      ...(passwordPolicy.RequireSymbols ? [{ label: 'At least one special character', met: /[^A-Za-z0-9]/.test(pw) }] : []),
    ];
  }, [passwordPolicy, passwordData.newPassword]);

  const policyMet = !passwordPolicy || getPasswordRequirements().every((r) => r.met);

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validatePasswordForm = () => {
    const errors = {};

    if (!passwordData.currentPassword) {
      errors.currentPassword = 'Current password is required';
    }

    if (!passwordData.newPassword) {
      errors.newPassword = 'New password is required';
    } else if (passwordData.newPassword.length < 8) {
      errors.newPassword = 'New password must be at least 8 characters';
    }

    if (!passwordData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your new password';
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (!validatePasswordForm()) {
      toastService.error('Please fix the errors in the form.');
      return;
    }

    setIsChangingPassword(true);
    setPasswordMessage('');

    try {
      await changePassword(passwordData.currentPassword, passwordData.newPassword);

      const msg = 'Password changed successfully! Signing out of all devices...';
      setPasswordMessage(msg);
      toastService.success(msg, 2000);

      // Wait a moment to show the message
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Sign out of all devices/sessions
      await globalSignOut();

    } catch (error) {
      const errorMsg = error.message || 'Error changing password';
      setPasswordMessage(`Error changing password: ${errorMsg}`);
      toastService.error(errorMsg);
      console.error('Password change error:', error);
    } finally {
      setIsChangingPassword(false);
    }
  };


  const getFieldError = (fieldName) => {
    return validationErrors[fieldName] || '';
  };


  if (isLoading) {
    return (
      <div className="security-tab">
        <div className="loading-container">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading security settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="security-tab">
      <div className="security-header">
        <h3>Security Settings</h3>
        <p>Manage your account security and privacy</p>
      </div>

      <div className="security-content">
        {/* Password Change Section */}
        <div className="security-section">
          <div className="section-header">
            <h4>Change Password</h4>
            {!showPasswordForm && (
              <button
                className="action-button"
                onClick={() => setShowPasswordForm(true)}
              >
                <i className="fas fa-key"></i>
                Change Password
              </button>
            )}
          </div>

          {showPasswordForm && (
            <form className="password-form" onSubmit={handlePasswordSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="currentPassword">Current Password</label>
                  <input
                    type="password"
                    id="currentPassword"
                    value={passwordData.currentPassword}
                    onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                    placeholder="Enter your current password"
                    disabled={isChangingPassword}
                    className={getFieldError('currentPassword') ? 'error' : ''}
                  />
                  {getFieldError('currentPassword') && (
                    <span className="error-message">{getFieldError('currentPassword')}</span>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="newPassword">New Password</label>
                  <input
                    type="password"
                    id="newPassword"
                    value={passwordData.newPassword}
                    onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                    placeholder="Enter your new password"
                    disabled={isChangingPassword}
                    className={getFieldError('newPassword') ? 'error' : ''}
                  />
                  {getFieldError('newPassword') && (
                    <span className="error-message">{getFieldError('newPassword')}</span>
                  )}
                  {passwordPolicy && (
                    <ul className="password-requirements">
                      {getPasswordRequirements().map((req) => (
                        <li key={req.label} className={req.met ? 'met' : 'unmet'}>
                          <i className={`fas ${req.met ? 'fa-check' : 'fa-times'}`}></i>
                          {req.label}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm New Password</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                    placeholder="Confirm your new password"
                    disabled={isChangingPassword}
                    className={getFieldError('confirmPassword') ? 'error' : ''}
                  />
                  {getFieldError('confirmPassword') && (
                    <span className="error-message">{getFieldError('confirmPassword')}</span>
                  )}
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className="save-button"
                  disabled={isChangingPassword || !policyMet}
                >
                  {isChangingPassword ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Changing Password...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save"></i>
                      Change Password
                    </>
                  )}
                </button>
                <button
                  type="button"
                  className="cancel-button"
                  onClick={() => setShowPasswordForm(false)}
                  disabled={isChangingPassword}
                >
                  <i className="fas fa-times"></i>
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* Password Message */}
          {passwordMessage && (
            <div className={`message ${passwordMessage.includes('Error') ? 'error' : 'success'}`}>
              <i className={`fas ${passwordMessage.includes('Error') ? 'fa-exclamation-triangle' : 'fa-check-circle'}`}></i>
              <span>{passwordMessage}</span>
            </div>
          )}
        </div>

        {/* MFA Section
        <div className="security-section">
          <div className="section-header">
            <h4>Multi-Factor Authentication</h4>
            <div className="mfa-status">
              <span className={`status-badge ${mfaEnabled ? 'enabled' : 'disabled'}`}>
                {mfaEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          </div>

          <p>Add an extra layer of security to your account with two-factor authentication.</p>

          <button
            className={`mfa-toggle-button ${mfaEnabled ? 'disable' : 'enable'}`}
            onClick={handleToggleMfa}
            disabled={isTogglingMfa}
          >
            {isTogglingMfa ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                {mfaEnabled ? 'Disabling...' : 'Enabling...'}
              </>
            ) : (
              <>
                <i className={`fas ${mfaEnabled ? 'fa-lock-open' : 'fa-lock'}`}></i>
                {mfaEnabled ? 'Disable MFA' : 'Enable MFA'}
              </>
            )}
          </button>
        </div> */}

        {/* TODO: Reimplement at a later date */}
        {/* Active Sessions Section
        <div className="security-section">
          <div className="section-header">
            <h4>Active Sessions</h4>
            <button
              className="action-button danger"
              onClick={handleSignOutAll}
            >
              <i className="fas fa-sign-out-alt"></i>
              Sign Out All Sessions
            </button>
          </div>

          <div className="sessions-list">
            {sessions.map(session => (
              <div key={session.id} className="session-item">
                <div className="session-info">
                  <div className="session-device">
                    <strong>{session.device}</strong>
                    {session.isCurrent && <span className="current-badge">Current</span>}
                  </div>
                  <div className="session-details">
                    <span className="session-location">
                      <i className="fas fa-map-marker-alt"></i>
                      {session.location}
                    </span>
                    <span className="session-time">
                      <i className="fas fa-clock"></i>
                      {formatLastActive(session.lastActive)}
                    </span>
                  </div>
                </div>
                
                {!session.isCurrent && (
                  <button
                    className="revoke-button"
                    onClick={() => handleRevokeSession(session.id)}
                  >
                    <i className="fas fa-times"></i>
                    Revoke
                  </button>
                )}
              </div>
            ))}
          </div>
        </div> */}

        {/* Data Management Section
        <div className="security-section">
          <h4>Data Management</h4>

          <div className="data-actions">
            <button
              className="action-button"
              onClick={handleDownloadData}
            >
              <i className="fas fa-download"></i>
              Download My Data
            </button>
          </div>
        </div> */}

        {/* Danger Zone
        <div className="security-section danger-zone">
          <h4>Danger Zone</h4>
          <p>These actions are irreversible and will permanently delete your account and all associated data.</p>

          <button
            className="delete-account-button"
            onClick={handleDeleteAccount}
          >
            <i className="fas fa-trash"></i>
            Delete My Account
          </button>
        </div> */}
      </div>
    </div>
  );
}
